/**
 * Google Analytics 4 service for BamakoFuel
 * Handles page views, events, and user tracking
 */

declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
    dataLayer: any[];
  }
}

class GoogleAnalyticsService {
  private measurementId: string;
  private isProduction: boolean;

  constructor() {
    this.measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  /**
   * Check if GA is properly initialized
   */
  isInitialized(): boolean {
    return !!(this.measurementId && window.gtag && this.isProduction);
  }

  /**
   * Track page views
   * @param path - The page path (e.g., '/stations', '/map')
   * @param title - Optional page title
   */
  trackPageView(path: string, title?: string): void {
    if (!this.isInitialized()) {
      console.log('GA Dev Mode - Page view:', path, title);
      return;
    }

    window.gtag('config', this.measurementId, {
      page_path: path,
      page_title: title,
    });
  }

  /**
   * Track custom events
   * @param eventName - Name of the event
   * @param parameters - Event parameters
   */
  trackEvent(eventName: string, parameters: Record<string, any> = {}): void {
    if (!this.isInitialized()) {
      console.log('GA Dev Mode - Event:', eventName, parameters);
      return;
    }

    window.gtag('event', eventName, {
      ...parameters,
      // Add app-specific context
      app_name: 'BamakoFuel',
      app_version: process.env.REACT_APP_VERSION || '1.0.0',
    });
  }

  /**
   * Track station interactions
   */
  trackStationEvent(action: string, stationName?: string, stationBrand?: string): void {
    this.trackEvent('station_interaction', {
      event_category: 'Station',
      event_label: stationName,
      action,
      brand: stationBrand,
    });
  }

  /**
   * Track fuel reports
   */
  trackFuelReport(fuelType: string, status: string, stationBrand?: string): void {
    this.trackEvent('fuel_report', {
      event_category: 'Report',
      fuel_type: fuelType,
      status,
      brand: stationBrand,
    });
  }

  /**
   * Track map interactions
   */
  trackMapEvent(action: string, details?: Record<string, any>): void {
    this.trackEvent('map_interaction', {
      event_category: 'Map',
      action,
      ...details,
    });
  }

  /**
   * Track search queries
   */
  trackSearch(query: string, resultCount?: number): void {
    this.trackEvent('search', {
      event_category: 'Search',
      search_term: query,
      result_count: resultCount,
    });
  }

  /**
   * Track user engagement time on page
   */
  trackEngagement(pageName: string, timeSpent: number): void {
    this.trackEvent('page_engagement', {
      event_category: 'Engagement',
      page_name: pageName,
      engagement_time_msec: timeSpent,
    });
  }

  /**
   * Track conversion events (successful reports, etc.)
   */
  trackConversion(conversionType: string, value?: number): void {
    this.trackEvent('conversion', {
      event_category: 'Conversion',
      conversion_type: conversionType,
      value: value || 1,
    });
  }

  /**
   * Set user properties
   */
  setUserProperty(propertyName: string, value: string): void {
    if (!this.isInitialized()) {
      console.log('GA Dev Mode - User property:', propertyName, value);
      return;
    }

    window.gtag('config', this.measurementId, {
      [`custom_map.${propertyName}`]: value,
    });
  }

  /**
   * Track errors
   */
  trackError(errorMessage: string, errorLocation?: string): void {
    this.trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      location: errorLocation,
    });
  }
}

// Export singleton instance
export const analytics = new GoogleAnalyticsService();

// Export hook for React components
export const useAnalytics = () => analytics;